"use client";

import { fontWeights } from "@/utils/fonts";
import { CircleCheckBig, Clock, Target, Users } from "lucide-react";
import { useRouter } from "next/navigation";

function CheckBullet({ text, variant }: { text: string; variant: "dark" | "light" }) {
  const iconColor = variant === "dark" ? "#FFFFFF" : "#002F6D";
  const textColor = variant === "dark" ? "#FFFFFF" : "#002F6D";
  return (
    <li className="flex items-start gap-2">
      <div className="" aria-hidden="true">
        <CircleCheckBig className="w-[16px] tsm-350:w-[16px] tsm-400:w-[18px] sm:w-[18px] md:w-[16px] lg:w-[18px] xl:w-[20px]" style={{ color: iconColor }} />
      </div>
      <span className="text-[12px] tsm-350:text-[13px] tsm-400:text-[13.5px] sm:text-[13px] md:text-[12px] lg:text-[14px]"
        style={{ color: textColor, fontFamily: "Asap", fontWeight: fontWeights.asap.regular }} >
        {text}
      </span>
    </li>
  );
}

function OfferCard({ badge, variant, title, subtitle, desc, bullets, ctaText, ctaVariant, iconBg, icon, badgeBg, badgeTextColor
}: {
  badge?: string;
  variant: "light" | "dark";
  title: string;
  subtitle: string;
  desc: string;
  bullets: string[];
  ctaText: string;
  ctaVariant: "primary" | "inverse";
  iconBg: string;
  icon: "clock" | "users" | "target";
  badgeBg?: string;
  badgeTextColor?: string;
}) {
  const router = useRouter();
  const isDark = variant === "dark";
  const Icon = icon === "clock" ? Clock : icon === "users" ? Users : Target;
  const iconFillColor = icon === "users" ? "#FFFFFF" : "#002F6D";
  return (
    <div
      className={[
        "relative w-full h-full flex flex-col overflow-visible rounded-[4px] border transition-all duration-300 hover:-translate-y-2 cursor-default hover:shadow-[0px_20px_50px_rgba(0,47,109,0.1)]",
        isDark ? "bg-[#002F6D] border-[#002F6D] text-white" : "bg-white border-[#E5E7EB] text-[#0F172A]",
      ].join(" ")}
    >
      {badge ? (
        <div className="absolute left-[80%] top-0 z-10"
          style={{ transform: "translate(-50%, -50%)" }}
        >
          <span
            className="inline-flex w-fit whitespace-nowrap items-center rounded-[4px]
              text-[8px] 
              tsm-350:text-[10px] 
              lg:text-[12px]  
              px-[5px] 
              tsm-350:px-[7px] 
              tsm-400:px-[8px] 
              md:px-[10px] 
              xl:px-[13px]  
              py-[4px] 
              tsm-350:py-[6px] 
              sm:py-[9px] 
              md:py-[6px] 
              lg:py-[9px] 
              xl:py-[11px]  
              h-[14px] 
              tsm-350:h-[18px] 
              tsm-400:h-[20px] 
              lg:h-[25px] 
              xl:h-[31px]"
            style={{ background: badgeBg || "#FFA500", color: badgeTextColor || "#002F6D", fontFamily: "Asap", fontWeight: fontWeights.asap.bold }}
          >
            {badge}
          </span>
        </div>
      ) : null}

      <div className="p-[15px] tsm-350:p-[15px] tsm-400:p-[15px] sm:p-[20px] md:p-[16px] lg:p-[25px] xl:p-[25px] 2xl:p-[33px] flex flex-col h-full">
        <div className="">
          <p
            className={[
              isDark ? "text-white/70 text-[11px] text-[10px] tsm-350:text-[11px] sm:text-[11px] md:text-[8.5px] lg:text-[11px] xl:text-[12px] mb-[10px] tsm-350:mb-[10px] tsm-400:mb-[15px] sm:mb-[20px] md:mb-[15px] lg:mb-[20px]" : "text-[#64748B]  text-[10px] tsm-350:text-[11px] md:text-[8.5px] lg:text-[11px] text-[12px]   tsm-350:mb-[15px] sm:mb-[20px] md:mb-[15px] lg:mb-[20px]",
            ].join(" ")}
            style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.medium, color: isDark ? "#FFFFFF" : "#475569" }}>
            {subtitle}
          </p>
        </div>

        <div className="flex flex-col items-start gap-0 lg:gap-3">
          <div
            className="flex items-center justify-center bg-[E8F0FE] 
            rounded-[10px] 
            lg:rounded-[12px] 
            xl:rounded-[14px]  
            w-[25px] 
            tsm-350:w-[30px]
            tsm-400:w-[35px] 
            sm:w-10 md:w-10 
            xl:w-12 
            h-[25px] 
            tsm-350:h-[30px] 
            tsm-400:h-[35px] 
            sm:h-10 
            xl:h-12  
            mb-2 
            tsm-350:mb-[10px] 
            tsm-400:mb-[16px] 
            sm:mb-[18px] 
            md:mb-[14px] 
            lg:mb-[18px]"
            style={{ background: iconBg }}
          >
            <Icon className="w-[14px] tsm-350:w-[18px] sm:w-[20px] md:w-[20px] xl:w-[24px]" color={iconFillColor} strokeWidth={2} aria-hidden="true" />
          </div>

          <h3 className="leading-[25px] xl:leading-[30px]
                 mb-3 
                 tsm-400:mb-[16px] 
                 sm:mb-[15px] 
                 text-[15px] 
                 tsm-400:text-[20px] 
                 sm:text-[22px] 
                 md:text-[18px] 
                 lg:text-[22px] 
                 xl:text-[24px]"
            style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.extrabold, color: isDark ? "#FFFFFF" : "#002F6D" }}
          >
            {title}
          </h3>
        </div>

        <p
          className="
            mb-3 
            tsm-350:mb-[16px] 
            sm:mb-[20px] 
            md:mb-[16px]
            lg:mb-[25px]   
            text-[13px] 
            tsm-400:text-[14px] 
            md:text-[13px] 
            lg:text-[14px] 
            xl:text-[16px]  
            leading-[20px] 
            md:leading-[18px] 
            lg:leading-[22px] 
            xl:leading-[26px]"
          style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular, color: isDark ? "#FFFFFF" : "#1A2B4A" }}
        >
          {desc}
        </p>

        <ul className="flex flex-col gap-0 tsm-350:gap-1 sm:gap-2 md:gap-0.5 lg:gap-1.5 xl:gap-2 2xl:gap-2.5">
          {bullets.map((b) => (
            <CheckBullet key={b} text={b} variant={isDark ? "dark" : "light"} />
          ))}
        </ul>

        <div className="mt-auto pt-0 lg:pt-4">
          <button
            type="button"
            className={["w-full py-[4px] tsm-350:py-[7px] tsm-400:py-[8px] sm:py-[10px] md:py-[8px] lg:py-[10px] text-[11px] tsm-350:text-[12px] tsm-400:text-[13px] sm:text-[13px] md:text-[13px] lg:text-[14px] rounded-[4px] transition-colors",
              ctaVariant === "inverse" ? "bg-white text-[#002F6D] hover:bg-white/90" : "bg-[#002F6D] text-white hover:bg-[#002F6D]/90"
            ].join(" ")}
            style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.semiBold }}
            onClick={() => window.dispatchEvent(new CustomEvent("openContactPopup"))}
          >
            {ctaText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FlexibleEngagementModels({
  title,
  items
}: {
  title?: string;
  items?: any[];
}) {
  const displayItems = items && items.length > 0 ? items : [];

  return (
    <section className="w-full -mt-[5px] tsm-350:-mt-[10px] tsm-400:-mt-[30px] sm:-mt-[50px] lg:-mt-[100px] xl:-mt-[130px] 2xl:-mt-[150px] relative z-20 pb-[20px]">
      <div className="mx-auto max-w-[1920px] px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1232px]">
          {title && (
            <h2
              className="text-center text-[#0F172A] tracking-normal 2xl:tracking-[-2px] 
                  text-[22px] 
                  sm:text-[32px] 
                  md:text-[35px] 
                  lg:text-[48px] 
                  xl:text-[60px]  
                  leading-[25px]  
                  tsm-350:leading-[30px] 
                  sm:leading-[40px] 
                  lg:leading-[50px] 
                  xl:leading-[60px] 
                  2xl:leading-[67px]"
              style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.regular }}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          )}

          <div className="pt-[15px] tsm-400:pt-[20px] sm:pt-[25px] md:pt-[30px] lg:pt-[40px] xl:pt-[50px]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 xl:gap-6 2xl:gap-8 items-stretch justify-center">
              {displayItems.map((tab, idx) => {
                const isActive = tab.isActive;

                return (
                  <div
                    key={idx}
                    className="flex justify-center"
                  >
                    <div className="w-full 
                            tsm-350:max-w-[280px] 
                            tsm-400:max-w-[320px] 
                            md:max-w-[389px]  
                            tsm-350:h-[380px] 
                            tsm-400:h-[420px] 
                            sm:h-[490px] 
                            md:h-[450px] 
                            lg:h-[490px] 
                            xl:h-[542px]
                          ">
                      <OfferCard
                        badge={tab.badge}
                        variant={isActive ? "dark" : "light"}
                        subtitle={tab.subtitle}
                        title={tab.title}
                        desc={tab.desc}
                        bullets={[...(tab.bullets || [])]}
                        ctaText={tab.ctaText}
                        ctaVariant={isActive ? "inverse" : "primary"}
                        iconBg={isActive ? "rgba(255,255,255,0.15)" : "#E8F0FE"}
                        icon={tab.icon as any}
                        badgeBg={tab.badgeBg}
                        badgeTextColor={tab.badgeTextColor}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

